import { useSelector } from 'react-redux';

export const coresFigma = {
    neutras: {
        branco: '#FFFFFF',
        cinzaClaro: '#F5F5F5',
        cinzaMedio: '#E0E0E0',
        cinzaEscuro: '#757575',
        preto: '#212121'
    }
};

function hexToHSL(hex) {
    // Remove o # se existir
    hex = hex.replace('#', '');

    // Converte para RGB
    const r = parseInt(hex.substring(0, 2), 16) / 255;
    const g = parseInt(hex.substring(2, 4), 16) / 255;
    const b = parseInt(hex.substring(4, 6), 16) / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h, s, l = (max + min) / 2;

    if (max === min) {
        h = s = 0; // achromatic
    } else {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

        switch (max) {
            case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
            case g: h = ((b - r) / d + 2) / 6; break;
            case b: h = ((r - g) / d + 4) / 6; break;
        }
    }

    return {
        h: Math.round(h * 360),
        s: Math.round(s * 100),
        l: Math.round(l * 100)
    };
}

/**
 * Converte HSL para hexadecimal
 * @param {number} h - Hue (0-360)
 * @param {number} s - Saturation (0-100)
 * @param {number} l - Lightness (0-100)
 * @returns {string} Cor em formato hexadecimal
 */
function hslToHex(h, s, l) {
    s /= 100;
    l /= 100;

    const c = (1 - Math.abs(2 * l - 1)) * s;
    const x = c * (1 - Math.abs((h / 60) % 2 - 1));
    const m = l - c / 2;
    let r = 0, g = 0, b = 0;

    if (0 <= h && h < 60) {
        r = c; g = x; b = 0;
    } else if (60 <= h && h < 120) {
        r = x; g = c; b = 0;
    } else if (120 <= h && h < 180) {
        r = 0; g = c; b = x;
    } else if (180 <= h && h < 240) {
        r = 0; g = x; b = c;
    } else if (240 <= h && h < 300) {
        r = x; g = 0; b = c;
    } else if (300 <= h && h < 360) {
        r = c; g = 0; b = x;
    }

    const toHex = (val) => {
        const hex = Math.round((val + m) * 255).toString(16);
        return hex.length === 1 ? '0' + hex : hex;
    };

    return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase();
}

/**
 * Gera 3 cores mais claras e 3 cores mais escuras a partir de uma cor base
 * @param {string} baseColor - Cor base em formato hexadecimal (ex: '#3AAEAE')
 * @returns {object} Objeto com arrays 'lighter' (mais claras) e 'darker' (mais escuras)
 */
export function generateColorVariations(baseColor) {
    const hsl = hexToHSL(baseColor);
    const variations = {
        lighter: [],
        darker: []
    };

    // Gera 3 cores mais claras
    // Incrementos progressivos de luminosidade: +15%, +25%, +35%
    const lighterIncrements = [15, 25, 35];
    for (let increment of lighterIncrements) {
        const newL = Math.min(100, hsl.l + increment);
        variations.lighter.push(hslToHex(hsl.h, hsl.s, newL));
    }

    // Gera 3 cores mais escuras
    // Decrementos progressivos de luminosidade: -15%, -25%, -35%
    const darkerIncrements = [15, 25, 35];
    for (let increment of darkerIncrements) {
        const newL = Math.max(0, hsl.l - increment);
        variations.darker.push(hslToHex(hsl.h, hsl.s, newL));
    }

    return variations;
}

/**
 * Custom Hook que gera um array de cores baseado na cor primária do Redux
 * Formato alternado: [COR ORIGINAL, neutro, cor clara 1, neutro, cor clara 2, cor escura 1, neutro, ...]
 * Similar ao padrão do coresMescladasArray para máximo contraste visual
 * @returns {array} Array com cores geradas + neutras alternadas
 */
export function useGeneratedColorArray() {
    const sessionSelector = useSelector((state) => {
        return state.core.session;
    });

    const { primary_color } = sessionSelector;
    
    if (!primary_color) {
        return coresMescladasArray; // Fallback para cores padrão
    }
    
    const variations = generateColorVariations(primary_color);

    // Cores neutras do coresFigma
    const neutralColors = [
        '#757575', // Neutro escuro
        '#F5F5F5', // Neutro claro
        '#E0E0E0', // Neutro médio
        '#212121', // Preto
        '#FFFFFF'  // Branco
    ];

    // Organiza as variações: [mais clara 3, mais clara 2, mais clara 1, mais escura 1, mais escura 2, mais escura 3]
    const lighterReversed = [...variations.lighter].reverse(); // Do mais claro ao menos claro
    const allVariations = [...lighterReversed, ...variations.darker];

    // Cria array alternado: cor original primeiro, depois alterna variações com neutras
    const result = [primary_color]; // Cor original sempre primeiro

    for (let i = 0; i < allVariations.length; i++) {
        result.push(allVariations[i]);
        // Adiciona cor neutra após cada variação (se ainda houver neutras disponíveis)
        if (i < neutralColors.length) {
            result.push(neutralColors[i]);
        }
    }

    // Adiciona neutras restantes no final, se houver
    if (neutralColors.length > allVariations.length) {
        result.push(...neutralColors.slice(allVariations.length));
    }

    return result;
}

// Array de cores padrão (fallback quando primary_color não está disponível)
export const coresMescladasArray = [
    '#3AAEAE', // Cor original
    '#757575', // Neutro escuro

    '#A8D4F4', // Azul
    '#F5F5F5', // Neutro claro
    '#212121', // Preto

    '#5ECFC1', // Verde
    '#7BBFF0', // Azul
    '#E0E0E0', // Neutro

    '#3CBFAF', // Verde
    '#4EAAEB', // Azul
    '#757575', // Neutro médio

    '#2AAFA0', // Verde
    '#2196E6', // Azul

    '#1A9D8F', // Verde
    '#0D82D3', // Azul

    '#0F8C7E', // Verde
    '#0A6EB8', // Azul

    '#0A5A9C', // Azul
    '#084A80', // Azul escuro

    '#8CDCD1' // Verde
];